import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import socketService from '../services/socket.service';
import { useSocketContext } from './SocketContext';

export interface BranchOption {
  value: number;
  label: string;
}

interface BranchContextType {
  // Single selection - chỉ cho phép chọn 1 chi nhánh
  selectedBranch: number | null;
  selectedBranchOption: BranchOption | null;
  setSelectedBranch: (branch: BranchOption | null) => void;
  clearBranch: () => void;
  isSubscribed: boolean;

  // Legacy support (deprecated - sẽ xóa sau)
  selectedBranches: number[];
  selectedBranchOptions: BranchOption[];
}

const BranchContext = createContext<BranchContextType>({
  selectedBranch: null,
  selectedBranchOption: null,
  setSelectedBranch: () => {},
  clearBranch: () => {},
  isSubscribed: false,

  // Legacy
  selectedBranches: [],
  selectedBranchOptions: [],
});

export const useBranchContext = () => useContext(BranchContext);

const STORAGE_KEY = 'selected_branches';

interface BranchProviderProps {
  children: React.ReactNode;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  // Single selection state
  const [selectedBranchOption, setSelectedBranchOptionState] = useState<BranchOption | null>(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { isConnected } = useSocketContext();

  // Derived values
  const selectedBranch = selectedBranchOption?.value ?? null;
  // Legacy support
  const selectedBranches = selectedBranch ? [selectedBranch] : [];
  const selectedBranchOptions = selectedBranchOption ? [selectedBranchOption] : [];

  // Bộ chọn chi nhánh (BranchSelector) đang tạm ẩn khỏi sidebar, nên không còn UI để
  // đổi/bỏ chọn chi nhánh. Vì vậy KHÔNG khôi phục lựa chọn cũ từ localStorage — nếu không
  // user từng chọn 1 chi nhánh sẽ bị kẹt lọc theo chi nhánh đó vĩnh viễn. Luôn khởi tạo về
  // null = "Tất cả chi nhánh". Xóa luôn key cũ để dọn state.
  // Khi mở lại BranchSelector, khôi phục đoạn load localStorage bên dưới.
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn('[BranchContext] Failed to clear localStorage:', e);
    }
  }, []);

  // Lưu vào localStorage khi thay đổi
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        branch: selectedBranch,
        option: selectedBranchOption,
      }));
    } catch (e) {
      console.warn('[BranchContext] Failed to save to localStorage:', e);
    }
  }, [selectedBranch, selectedBranchOption]);

  // Subscribe socket khi có branch được chọn và socket connected
  useEffect(() => {
    if (!isConnected || !selectedBranch) {
      setIsSubscribed(false);
      return;
    }

    console.log('[BranchContext] Subscribing to branch:', selectedBranch);

    // Subscribe to orders cho chi nhánh đã chọn
    socketService.subscribeToOrders([selectedBranch]);

    // Join branch room
    socketService.joinBranchRoom(selectedBranch);

    setIsSubscribed(true);

    // Cleanup: leave room khi unmount hoặc branch thay đổi
    return () => {
      socketService.leaveBranchRoom(selectedBranch);
    };
  }, [isConnected, selectedBranch]);

  const setSelectedBranch = useCallback((branch: BranchOption | null) => {
    setSelectedBranchOptionState(branch);
  }, []);

  const clearBranch = useCallback(() => {
    setSelectedBranchOptionState(null);
  }, []);

  return (
    <BranchContext.Provider
      value={{
        // Single selection
        selectedBranch,
        selectedBranchOption,
        setSelectedBranch,
        clearBranch,
        isSubscribed,

        // Legacy support
        selectedBranches,
        selectedBranchOptions,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

export default BranchContext;
